import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["accessibilityLabel", "checked", "children", "defaultValue", "disableClickableLabel", "disabled", "error", "focused", "hovered", "id", "isBold", "keyboardFocused", "legal", "name", "onBlur", "onChange", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "rightAligned", "testID", "UNSAFE_containerProps", "UNSAFE_inputProps", "warning"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import Text from '@bubble-ui/primitive-text';
import useStyleSheet from '@bubble-ui/hook-use-stylesheet';
import useUncontrolledProp from '@bubble-ui/hook-use-uncontrolled-prop';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import { useDesignContext } from '@klarna/designtokens';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import { useUniqueFormIdentifier } from '@bubble-ui/hook-use-unique-form-identifier';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { getIds } from './getIds';
import { defaultDesignTokens } from './defaultDesignTokens';

/**
 * @deprecated this type export will be removed with v6.0.0
 */

var getDefaultStyleSheet = _ref => {
  var {
    checked,
    disabled,
    error,
    keyboardFocused,
    legal,
    rightAligned,
    warning,
    disableClickableLabel,
    isBold,
    designContext,
    interactiveState
  } = _ref;
  var platform = 'default';
  var isChecked = checked ? 'checked' : 'default';
  var interactiveKnobState = disabled ? 'disabled' : 'default';
  var feedbackState = error ? 'error' : warning ? 'warning' : 'default';
  var textStyle = isBold ? 'bold' : legal ? 'legal' : 'default';
  var toggleBackgroundColor = designContext.get('toggle/background-color', {
    platform,
    feedbackState,
    isChecked,
    interactiveState
  });
  var toggleKnobColor = designContext.get('toggle/knob-color', {
    platform,
    feedbackState,
    isChecked,
    interactiveState: interactiveKnobState
  });
  var toggleTextColor = designContext.get('toggle/text-color', {
    interactiveState
  });
  var toggleTextStyles = designContext.get('toggle/text-style', {
    textStyle
  }).styles();
  var toggleKnobBorderRadius = designContext.get('toggle/knob-corner-radius').pixelValue;
  var toggleSwitchBorderRadius = designContext.get('toggle/switch-corner-radius').pixelValue;
  var toggleWidth = designContext.get('toggle/width').pixelValue;
  var toggleHeight = designContext.get('toggle/height').pixelValue;
  var toggleKnobSize = designContext.get('toggle/knob-size').pixelValue;
  var lineHeight = toggleTextStyles.lineHeight ? toggleTextStyles.lineHeight : toggleTextStyles.fontSize;
  var totalToggleHeight = Math.max(toggleHeight, toggleKnobSize);
  var minHeight = Math.max(totalToggleHeight, lineHeight);
  // how much space should be between the Knob and the outside of the Toggle
  var spaceBetweenKnobAndOutside = (toggleHeight - toggleKnobSize) / 2;
  // how many px do we need to move to the right when active
  var leftOffsetIfChecked = toggleWidth - toggleKnobSize - spaceBetweenKnobAndOutside * 2;
  // 8 = 2 times the width of keyboardFocus border + 2 times the space between keyboardFocus border and outside of knob
  var keyboardFocusSize = toggleKnobSize - 8;
  var labelOffset = toggleWidth + 10;
  return {
    mainStyle: {
      position: 'relative',
      minHeight
    },
    bulletStyle: {
      alignSelf: 'center',
      backgroundColor: toggleBackgroundColor.toString(),
      borderRadius: toggleSwitchBorderRadius,
      cursor: disabled ? 'auto' : 'pointer',
      height: toggleHeight,
      left: 0,
      position: 'relative',
      transition: 'background-color .2s ease, border-color .2s ease',
      width: toggleWidth
    },
    outerToggleStyle: {
      backgroundColor: toggleKnobColor.toString(),
      borderRadius: toggleKnobBorderRadius,
      cursor: disabled ? 'auto' : 'pointer',
      display: 'block',
      height: toggleKnobSize,
      left: spaceBetweenKnobAndOutside,
      position: 'absolute',
      top: spaceBetweenKnobAndOutside,
      transition: 'transform .2s ease',
      transform: checked ? "translateX(".concat(leftOffsetIfChecked, "px)") : 'none',
      width: toggleKnobSize,
      boxShadow: 'rgba(0, 0, 0, 0.1) 0 2px 2px 0'
    },
    innerToggleStyle: {
      backgroundColor: toggleKnobColor.toString(),
      borderRadius: toggleKnobBorderRadius,
      border: keyboardFocused ? "2px solid ".concat(toggleBackgroundColor.toString()) : 'none',
      cursor: disabled ? 'auto' : 'pointer',
      display: 'block',
      height: keyboardFocusSize,
      left: '2px',
      position: 'relative',
      top: '2px',
      width: keyboardFocusSize
    },
    containerStyle: _objectSpread(_objectSpread({}, rightAligned ? {
      right: 0
    } : {
      left: 0
    }), {}, {
      display: 'flex',
      minHeight,
      position: 'absolute'
    }),
    inputStyle: _objectSpread(_objectSpread({
      height: '100%',
      opacity: 0,
      position: 'absolute',
      width: disableClickableLabel ? toggleWidth : '100%',
      top: 0
    }, rightAligned ? {
      right: 0
    } : {
      left: 0
    }), {}, {
      zIndex: 1,
      cursor: disabled ? 'auto' : 'pointer',
      WebkitTapHighlightColor: designContext.get('colors/backgrounds/transparent', {}).toString(),
      margin: 0
    }),
    labelStyle: {
      cursor: disabled || disableClickableLabel ? 'auto' : 'pointer',
      display: 'block',
      position: 'relative',
      minHeight: disableClickableLabel ? undefined : minHeight
    },
    textStyle: _objectSpread(_objectSpread(_objectSpread({
      color: toggleTextColor.toString()
    }, toggleTextStyles), rightAligned ? {
      marginRight: labelOffset
    } : {
      marginLeft: labelOffset
    }), {}, {
      marginTop: 0,
      marginBottom: 0,
      textRendering: 'geometricPrecision',
      WebkitFontSmoothing: 'antialiased',
      display: 'block',
      paddingTop: (totalToggleHeight - lineHeight) / 2
    })
  };
};
var SwitchToggle = _ref2 => {
  var {
      accessibilityLabel,
      checked,
      children,
      defaultValue,
      disableClickableLabel,
      disabled,
      error,
      focused,
      hovered,
      id,
      isBold,
      keyboardFocused,
      legal,
      name,
      onBlur,
      onChange,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed,
      rightAligned,
      testID,
      UNSAFE_containerProps,
      UNSAFE_inputProps,
      warning
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'SwitchToggle',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var ids = getIds(id);
  var designContext = useDesignContext(defaultDesignTokens);
  var designVersion = useDesignVersion();
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined(_objectSpread(_objectSpread({}, props), {}, {
    disabled,
    focused,
    hovered,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    keyboardFocused
  }));
  var [uncontrolledValue, setUncontrolledValue] = useUncontrolledProp({
    prop: checked,
    defaultProp: defaultValue || false,
    onChange
  });
  var componentName = useUniqueFormIdentifier({
    name,
    fallbackName: 'SwitchToggle'
  });
  var uniqueComponentId = useUniqueFormIdentifier({
    fallbackName: name || 'SwitchToggle'
  });
  var uniqueTextContentId = useUniqueFormIdentifier({
    fallbackName: 'SwitchToggleLabel'
  });
  var componentId = (ids === null || ids === void 0 ? void 0 : ids.input) || uniqueComponentId;
  var {
    mainStyle,
    inputStyle,
    labelStyle,
    containerStyle,
    bulletStyle,
    outerToggleStyle,
    innerToggleStyle,
    textStyle
    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
  } = useStyleSheet(SwitchToggle, getDefaultStyleSheet, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, props), eventHandlers), interactiveStateCombined), {}, {
    accessibilityLabel,
    checked: uncontrolledValue,
    children,
    defaultValue,
    designContext,
    designVersion,
    disableClickableLabel,
    disabled,
    error,
    id,
    isBold,
    legal,
    name,
    onChange,
    rightAligned,
    testID,
    UNSAFE_containerProps,
    UNSAFE_inputProps,
    warning
  }));
  var textContentId = ids !== null && ids !== void 0 && ids.text ? ids === null || ids === void 0 ? void 0 : ids.text : disableClickableLabel ? uniqueTextContentId : undefined;
  var switchToggleTextChildren = /*#__PURE__*/React.createElement(Text, {
    id: textContentId,
    style: textStyle
  }, children);
  return /*#__PURE__*/React.createElement("div", _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root
  }, UNSAFE_containerProps, disableClickableLabel ? {} : {
    onFocus: eventHandlers.onFocus,
    onBlur: eventHandlers.onBlur,
    onMouseOver: eventHandlers.onMouseOver,
    onMouseOut: eventHandlers.onMouseOut
  }, props, {
    style: mainStyle
  }), /*#__PURE__*/React.createElement("input", _extends({
    id: componentId
  }, UNSAFE_inputProps, {
    name: componentName,
    "aria-label": accessibilityLabel,
    "aria-labelledby": accessibilityLabel ? undefined : textContentId,
    type: "checkbox",
    checked: uncontrolledValue,
    disabled: disabled,
    onChange: disabled ? undefined : e => setUncontrolledValue(!uncontrolledValue, e),
    style: inputStyle,
    onMouseDown: eventHandlers.onMouseDown,
    onMouseUp: eventHandlers.onMouseUp
  })), /*#__PURE__*/React.createElement("label", {
    id: ids === null || ids === void 0 ? void 0 : ids.label,
    htmlFor: componentId,
    style: labelStyle
  }, /*#__PURE__*/React.createElement("div", {
    id: ids === null || ids === void 0 ? void 0 : ids.container,
    style: containerStyle
  }, /*#__PURE__*/React.createElement("div", {
    id: ids === null || ids === void 0 ? void 0 : ids.bullet,
    style: bulletStyle
  }, /*#__PURE__*/React.createElement("div", {
    id: ids === null || ids === void 0 ? void 0 : ids.outerToggleWrapper,
    style: outerToggleStyle
  }, /*#__PURE__*/React.createElement("div", {
    id: ids === null || ids === void 0 ? void 0 : ids.innerToggleWrapper,
    style: innerToggleStyle
  })))), !disableClickableLabel && switchToggleTextChildren), disableClickableLabel && switchToggleTextChildren);
};
SwitchToggle.displayName = 'SwitchToggle';
export default withProvisionalOverrideFromContext(SwitchToggle);
//# sourceMappingURL=index.js.map